import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Button } from "../../components/ui/Button";
import { useI18n } from "../../i18n/I18nProvider";
import { ProgressBar } from "../../components/ui/ProgressBar";

import { playerApi, type PlayerPanelProps, type SongPhase } from "./api";
import { errorMessageKey, saveRegion, uploadSong } from "./actions";
import { RegionEditor } from "./RegionEditor";
import type { ClampedRegion } from "./region";

const SONG_ACCEPT = ".mp3,.m4a,.aac,audio/mpeg,audio/mp4,audio/aac";
/** Room the fixed Save bar takes at the bottom of a phone (matches the page padding in PlayerPage). */
const SAVE_BAR_CLEARANCE = "calc(4.5rem + env(safe-area-inset-bottom))";

type Translate = ReturnType<typeof useI18n>["t"];

function describePhase(phase: SongPhase, t: Translate): string {
  switch (phase.kind) {
    case "prepare":
      return t("player.preparing");
    case "upload":
      return t("player.uploading", { percent: Math.round(phase.fraction * 100) });
  }
}

function phaseFraction(phase: SongPhase): number {
  return phase.kind === "upload" ? phase.fraction : 0;
}

/** Padlock icon for the operator-set song note. Decorative: the text carries the meaning. */
function LockIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/**
 * Section readout and waveform first, then the song file control below it, then the pinned save
 * bar. Errors sit under the control they belong to.
 */
export function SongPanel({ token, profile, onProfile, onFailure, onSuccess }: PlayerPanelProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<SongPhase | null>(null);
  const [draft, setDraft] = useState<ClampedRegion | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMoment, setSavedMoment] = useState(0);
  const locked = profile.locked;
  const songLocked = profile.songLocked;
  const busy = phase !== null || saving;
  const songId = profile.songMediaId;
  const savedLengthMs = profile.lengthS * 1000;
  const dirty =
    draft !== null && (draft.start_ms !== profile.startMs || draft.length_ms !== savedLengthMs);
  const showSave = dirty && !locked && !songLocked && songId !== null;

  // On phones the Save bar is fixed to the bottom: focus scrolls land above it, not under it.
  useEffect(() => {
    if (!showSave || (window.matchMedia?.("(min-width: 1024px)").matches ?? false)) {
      return undefined;
    }
    const root = document.documentElement;
    const previous = root.style.scrollPaddingBottom;
    root.style.scrollPaddingBottom = SAVE_BAR_CLEARANCE;
    return () => {
      root.style.scrollPaddingBottom = previous;
    };
  }, [showSave]);

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    setError(null);
    try {
      const next = await uploadSong(token, file, setPhase);
      onProfile(next);
      onSuccess("player.songSaved");
    } catch (caught: unknown) {
      setError(t(errorMessageKey(caught)));
      onFailure(caught);
    } finally {
      setPhase(null);
    }
  }

  async function handleSave() {
    if (!draft) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onProfile(await saveRegion(token, draft));
      setSavedMoment((moment) => moment + 1);
      onSuccess("player.regionSaved");
    } catch (caught: unknown) {
      setError(t(errorMessageKey(caught)));
      onFailure(caught);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      aria-labelledby="song-heading"
      className="flex flex-col gap-4 rounded-panel bg-board p-4 shadow-board"
    >
      <h2 id="song-heading" className="font-display text-2xl leading-none font-extrabold uppercase">
        {t("player.song")}
      </h2>
      {songLocked ? (
        <p className="flex items-center gap-2 text-operate text-ink-2">
          <LockIcon />
          {t("player.songLockedNote")}
        </p>
      ) : null}
      {!songLocked && songId !== null && profile.songLabel ? (
        <p className="text-note text-ink-2">{profile.songLabel}</p>
      ) : null}
      {songId !== null ? (
        <RegionEditor
          key={songId}
          url={playerApi.mediaUrl(token, songId)}
          saved={{ start_ms: profile.startMs, length_ms: savedLengthMs }}
          locked={locked}
          songLocked={songLocked}
          savedMoment={savedMoment}
          onDraftChange={setDraft}
        />
      ) : songLocked ? null : (
        <p className="text-operate text-ink-2">{t("player.songNone")}</p>
      )}
      {songLocked ? null : (
        <div className="flex flex-col gap-3">
          <input
            ref={inputRef}
            type="file"
            accept={SONG_ACCEPT}
            tabIndex={-1}
            className="sr-only"
            aria-label={t("player.songFile")}
            disabled={locked || busy}
            onChange={(event) => void handleFile(event)}
          />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button
              variant="secondary"
              disabled={locked || busy}
              onClick={() => inputRef.current?.click()}
            >
              {songId !== null ? t("player.songReplace") : t("player.songUpload")}
            </Button>
            <p className="text-note text-ink-3">{t("player.songHint")}</p>
          </div>
        </div>
      )}
      {error ? (
        <p role="alert" className="text-note font-medium text-red">
          {error}
        </p>
      ) : null}
      {phase ? (
        <ProgressBar fraction={phaseFraction(phase)} label={describePhase(phase, t)} />
      ) : null}
      {showSave ? (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-hairline bg-board px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-board lg:static lg:border-0 lg:px-0 lg:pt-0 lg:pb-0 lg:shadow-none">
          <div className="mx-auto max-w-md lg:max-w-none">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              loading={saving}
              disabled={busy}
              onClick={() => void handleSave()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
